/**
 * Scripted, deterministic capture flow for the family profile (no NLU).
 * Mail or a flyer comes first. Ages are one bubble, asked once a mail
 * needs the children. Age band → birth month midpoint, kept in sync with
 * server/lib/familyProfile.js and server/lib/familyChildren.js.
 */

import { newChildId } from "./familyChildren";

/** Age band chips in the capture bubble. */
export const AGE_BAND_OPTIONS = [
  { value: "baby", label: "0–1 Jahr" },
  { value: "toddler", label: "1–3 Jahre" },
  { value: "preschool", label: "3–6 Jahre" },
  { value: "school", label: "Über 6" },
];

const BAND_MIDPOINT_MONTHS = { baby: 6, toddler: 24, preschool: 54, school: 96 };

/** Birth month ("YYYY-MM") for the midpoint of an age band. */
export function birthYmForBand(band, now = new Date()) {
  const months = BAND_MIDPOINT_MONTHS[band];
  if (months == null) return null;
  const d = new Date(now.getFullYear(), now.getMonth() - months, 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

export function ageBandLabel(band) {
  return AGE_BAND_OPTIONS.find((o) => o.value === band)?.label || "";
}

export const PROFILE_FLOW_TEXT = {
  askAge: "Kurz das Alter — tippe jedes Kind an.",
  askMore: "Notiert. Noch eines — sonst weiter.",
  ageSkip: "Später",
  ageDone: "Fertig",
  ageRemove: "Entfernen",
  askPlz:
    "Und wo seid ihr zu Hause? Mit eurer Postleitzahl wird das Wetter im Briefing genau für euer Quartier.",
  plzInvalid:
    "Das habe ich nicht als Postleitzahl erkannt. Tippe eine PLZ an oder gib sie ein (z. B. 8004) — oder überspringe diesen Schritt.",
  ageHint: "Tippe ein Alter an — oder später.",
};

/** Confirmation message after saving. */
export function buildProfileDoneText({
  childCount,
  recapLines = [],
  quartier,
  postalCode,
}) {
  if (!childCount) {
    return "Alles klar — im Chat unter Kinder nachtragen.";
  }
  const kids =
    Array.isArray(recapLines) && recapLines.length > 0
      ? recapLines.join("; ")
      : childCount === 1
        ? "1 Kind notiert"
        : `${childCount} Kinder notiert`;
  let where = "";
  if (quartier) where = ` Wetter ab morgen für ${quartier}.`;
  else if (postalCode) where = ` Wetter ab morgen für PLZ ${postalCode}.`;
  return `Merci — ${kids}.${where} Du kannst das jederzeit im Chat anpassen.`;
}

/**
 * One child row for the age bubble. Under 3 is KiTa. 3–6 and Über 6
 * stay unset so KiTa/Kindergarten is asked only for 3–6, and only once
 * a mail needs it.
 * @param {string} band
 * @param {Date} [now]
 */
export function childDraftFromBand(band, now = new Date()) {
  const birthYm = birthYmForBand(band, now);
  if (!birthYm) return null;
  const child = { id: newChildId(), birth_ym: birthYm };
  if (band === "baby" || band === "toddler") child.setting = "kita";
  return child;
}

/**
 * Age bubble after a mail or flyer, for a household that never finished
 * or skipped the profile. `profileReady` false means no completed_at and
 * no saved children.
 *
 * @param {{
 *   profileReady?: boolean | null,
 *   hasExtractions?: boolean | null,
 *   flowActive?: boolean,
 * }} [args]
 */
export function shouldOfferDeferredAgeCapture(args = {}) {
  if (args.profileReady !== false) return false;
  if (args.hasExtractions !== true) return false;
  if (args.flowActive) return false;
  return true;
}
